import React, { useState, useEffect, useRef } from 'react';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { Save } from 'lucide-react';

// --- IMPORT RESPONSIVE DIRECTLY ---
import { Responsive as ResponsiveGridLayout } from 'react-grid-layout';

// 1. Import the Widget Factory & API
import { renderDashboardWidget, GlobalBroadcastListener } from '../components/widgets/DashboardWidgets';
import { fetchClientsList } from '../services/nommiaApi'; // <-- ADDED API IMPORT
import { supabase } from '../supabaseClient';

// 2. Import the Role Configurations
import { getRoleConfig } from '../data/RoleConfig';

// 3. Import the Stats & Modals
import DashboardStats from '../components/dashboard/DashboardStats';
import { DashboardModals } from '../components/widgets/DashboardModals';

const Dashboard = ({ userRole }) => { 
    const [layout, setLayout] = useState([]);
    
    // Dynamic Width State
    const [containerWidth, setContainerWidth] = useState(1200);
    const dashboardRef = useRef(null);

    // --- THE FIX: ADD OVERRIDE STATE FOR THE DROPDOWN ---
    const [overrideRole, setOverrideRole] = useState(userRole || 'Admin');

    // Load config based on the override dropdown (instead of just the hardcoded userRole)
    const roleKey = overrideRole;
    const config = getRoleConfig(roleKey);

    // --- ACTION CONTROLLER STATE ---
    const [activeModal, setActiveModal] = useState(null);
    const [modalData, setModalData] = useState(null);
    
    // --- LIVE METRICS STATE ---
    const [liveMetrics, setLiveMetrics] = useState(null); 
    const [myTickets, setMyTickets] = useState([]); 
    const [unassignedTickets, setUnassignedTickets] = useState([]);
    const [completedTickets, setCompletedTickets] = useState([]); // <-- NEW: Completed Tickets State

    // --- LAYOUT SAVING STATE ---
    const [isSavingLayout, setIsSavingLayout] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

    // --- FETCH & CRUNCH LIVE DATA ---
    useEffect(() => {
        const fetchLiveMetrics = async () => {
            try {
                const data = await fetchClientsList(1, 500);
                const users = data.users || [];
                const totalUsers = data.pagination?.total || data.totalCount || data.total || users.length;
                const kycApproved = users.filter(u => u.kyc?.status === 'VERIFIED' || u.kyc?.attemptStatus === 'APPROVED').length;
                const premiumUsers = users.filter(u => u.membership?.planName === 'Pro' || u.membership?.planName === 'Elite').length;

                setLiveMetrics({
                    totalUsers,
                    kycApproved,
                    premiumUsers,
                    kycConversionRate: totalUsers > 0 ? Math.round((kycApproved / totalUsers) * 100) : 0
                });
            } catch (error) {
                console.error("Failed to fetch live dashboard metrics:", error);
            }
        };

        // --- UPDATED: ROLE-AWARE TICKET FETCHER ---
        const fetchLiveTickets = async () => {
            try {
                // Determine if viewing as Management
                const isManager = roleKey === 'Admin' || roleKey.includes('Manager');
                
                // Placeholder for actual logged-in user ID later (e.g., user.id)
                const currentAgentId = 'Agent_Name'; 

                // 1. Fetch Active/Assigned Tickets
                let activeQuery = supabase.from('crm_tickets').select('*').neq('status', 'Closed').order('created_at', { ascending: false });
                
                if (isManager) {
                    // Managers see ALL active assigned tickets across the team
                    activeQuery = activeQuery.not('assigned_to', 'is', null);
                } else {
                    // Standard Agents only see their personal tickets (or the AI queue)
                    activeQuery = activeQuery.eq('assigned_to', currentAgentId); 
                }
                const { data: myData } = await activeQuery;
                if (myData) setMyTickets(myData);

                // 2. Fetch Unassigned Pool (Standard tickets waiting to be pulled - same for everyone)
                const { data: unassignedData } = await supabase
                    .from('crm_tickets')
                    .select('*')
                    .is('assigned_to', null)
                    .neq('status', 'Closed')
                    .order('created_at', { ascending: false });
                if (unassignedData) setUnassignedTickets(unassignedData);

                // 3. Fetch Completed Tickets (NEW)
                let completedQuery = supabase.from('crm_tickets').select('*').eq('status', 'Closed').order('created_at', { ascending: false }).limit(50);
                
                if (!isManager) {
                    // Agents only see their own completed tickets
                    completedQuery = completedQuery.eq('assigned_to', currentAgentId);
                }
                const { data: completedData } = await completedQuery;
                if (completedData) setCompletedTickets(completedData);

            } catch (error) {
                console.error("Failed to fetch live tickets:", error);
            }
        };

        fetchLiveMetrics();
        fetchLiveTickets(); 
        
    // 👇 CRITICAL: Adding roleKey here means when you change the dropdown, it instantly refetches the correct data!
    }, [roleKey]);

    // This function is the "Universal Remote" for all widgets
    const handleWidgetAction = (action, data = null) => {
        console.log("Action Triggered:", action, data); 

        switch (action) {
            case 'OPEN_BROADCAST':
                setActiveModal('broadcast');
                break;
            case 'START_REVIEW':
                setModalData(data);
                setActiveModal('review');
                break;
            case 'TOGGLE_MONITOR':
                setModalData({ ...data, initialMode: 'listen' }); // Default mode
                setActiveModal('monitor');
                break;
            case 'TOGGLE_WHISPER':
                setModalData({ ...data, initialMode: 'whisper' }); // Tell modal to focus input
                setActiveModal('monitor'); // Open the same modal
                break;
            default:
                console.warn("Unknown action:", action);
        }
    }; 

    // --- 1. DYNAMIC WIDTH CALCULATION ---
    useEffect(() => {
        const handleResize = () => {
            if (dashboardRef.current) {
                setContainerWidth(dashboardRef.current.offsetWidth - 48);
            }
        };
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // --- 2. SMART LAYOUT GENERATOR & DB SYNC ---
    useEffect(() => {
        const loadLayout = async () => {
            // 1. Check if user has a custom saved layout
            try {
                const { data: { user } } = await supabase.auth.getUser();
                if (user) {
                    const { data, error } = await supabase
                        .from('crm_dashboard_layouts')
                        .select('layout')
                        .eq('user_id', user.id)
                        .eq('role', roleKey)
                        .maybeSingle(); // 🚨 FIXED: Now it returns null instead of a 406 error!

                    // If we got a layout back, use it and exit early!
                    if (data && data.layout) {
                        setLayout(data.layout);
                        setHasUnsavedChanges(false);
                        return; 
                    }
                    
                    // If error exists and it's NOT just a missing row, log it
                    if (error && error.code !== 'PGRST116') {
                        console.error("Supabase layout fetch error:", error);
                    }
                }
            } catch (e) {
                console.error("Failed to load custom layout:", e);
            }
            // Standard generated layouts
            let currentX = 0;
            let currentY = 0;
            const totalCols = 4;
            let layoutWidgets = [...(config?.dashboardLayout || [])];

            const generatedLayout = layoutWidgets.map((widgetId) => {
                let width = 1; 
                let height = 3; // <--- Changed to 'let' so we can modify it

                if (roleKey === 'Support_Manager' && widgetId === 'live_floor') width = 4;
                if (roleKey === 'Support_Manager' && ['ticket_queue', 'kyc_queue'].includes(widgetId)) width = 2;
                if (widgetId === 'review_calendar' || widgetId === 'contact_queue') width = 2;
                if (roleKey === 'Support_L1' && ['ticket_queue', 'kyc_queue'].includes(widgetId)) width = 2;
                if (widgetId === 'hybrid_unified') width = 4;

                // --- NEW: HYBRID AGENT CUSTOM DIMENSIONS ---
                if (roleKey.includes('Hybrid')) {
                    if (widgetId === 'ticket_queue' || widgetId === 'contact_queue') width = 3;
                    if (widgetId === 'kyc_queue') height = 6;
                }

                if (currentX + width > totalCols) {
                    currentX = 0;
                    currentY++;
                }

                const item = { i: widgetId, x: currentX, y: currentY, w: width, h: height };
                currentX += width;
                return item;
            });

            setLayout(generatedLayout);
            setHasUnsavedChanges(false);
        };

        loadLayout();
    }, [roleKey, config]);

    // Save Function for Layouts
    const saveLayoutToDatabase = async () => {
        setIsSavingLayout(true);
        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) throw new Error("Must be logged in to save layout.");

            const { error } = await supabase
                .from('crm_dashboard_layouts')
                .upsert({ user_id: user.id, role: roleKey, layout: layout }, { onConflict: 'user_id, role' });

            if (error) throw error;
            setHasUnsavedChanges(false);
            alert("Dashboard layout saved successfully!");
        } catch (error) {
            console.error("Save Layout Error:", error);
            alert("Failed to save your dashboard layout.");
        } finally {
            setIsSavingLayout(false);
        }
    };
    
   return (
        <div ref={dashboardRef} className="p-6 w-full min-h-screen relative">
            
            {/* THE FIX: The Global Listener sits invisibly at the top of the dashboard */}
            <GlobalBroadcastListener />
            
            {/* Header Section */}
            <div className="mb-8">
                <h1 className="text-3xl font-bold text-white mb-2">Morning Briefing</h1>
                <div className="flex justify-between items-end">
                    <p className="text-slate-400">Welcome back. Here is what's happening on the floor today.</p>
                    
                    {/* THE FIX: SAVE BUTTON & INTERACTIVE ROLE TOGGLE */}
                    <div className="flex items-center gap-3">
                        {hasUnsavedChanges && (
                            <button 
                                onClick={saveLayoutToDatabase} 
                                disabled={isSavingLayout}
                                className="bg-green-600 hover:bg-green-500 text-white text-xs font-bold px-4 py-2 rounded-lg flex items-center gap-2 transition-all shadow-lg shadow-green-900/20"
                            >
                                <Save size={14} /> {isSavingLayout ? 'Saving...' : 'Save Layout'}
                            </button>
                        )}
                        
                        {/* 🚨 THE SECURITY LOCK: Only render this dropdown if their REAL role is Admin or Manager */}
                        {(userRole === 'Admin' || String(userRole).includes('Manager')) && (
                            <div className="flex items-center gap-3 bg-slate-900 px-4 py-2 rounded-lg border border-slate-800">
                                <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse"></span>
                                <span className="text-xs text-slate-400 font-bold uppercase tracking-wider">View As:</span>
                                <select 
                                    value={overrideRole}
                                    onChange={(e) => setOverrideRole(e.target.value)}
                                    className="bg-slate-800 text-slate-200 border border-slate-700 rounded p-1 text-sm focus:ring-1 focus:ring-blue-500 outline-none cursor-pointer font-bold"
                                >
                                    <option value="Admin">Admin (God Mode)</option>
                                    <option value="Sales_Manager">Head of Sales</option>
                                    <option value="Sales_L1">Junior Sales Agent</option>
                                    <option value="Support_Manager">Support Lead</option>
                                    <option value="Support_L1">Customer Support</option>
                                    <option value="Hybrid_Agent">Sales & Support Associate</option>
                                </select>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* --- HEADS UP DISPLAY (Now receiving live data!) --- */}
            <DashboardStats role={roleKey} liveMetrics={liveMetrics} />
            
            {/* Draggable Widget Grid */}
            <ResponsiveGridLayout
                key={roleKey} 
                className="layout"
                layouts={{ lg: layout }}
                onLayoutChange={(newLayout) => {
                    setLayout(newLayout);
                    setHasUnsavedChanges(true); // Triggers the Save button to appear
                }}
                breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
                cols={{ lg: 4, md: 4, sm: 2, xs: 1, xxs: 1 }} 
                rowHeight={100}
                width={containerWidth} 
                isDraggable={true}
                isResizable={false} 
                margin={[16, 16]}
            >
                {layout.map(item => (
                    <div key={item.i} className="h-full">
                        {renderDashboardWidget(item.i, roleKey, handleWidgetAction, { 
                            tickets: myTickets, 
                            unassigned: unassignedTickets,
                            completed: completedTickets
                        })}
                    </div>
                ))}
            </ResponsiveGridLayout>

            {/* --- ACTION LAYER: This MUST be here for the popup to show --- */}
            <DashboardModals 
                activeModal={activeModal} 
                onClose={() => setActiveModal(null)} 
                modalData={modalData}
            />
        </div>
    );
};

export default Dashboard;